import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { HeroWave } from "@/components/HeroWave";

export function KineticHero() {
  const reduced = useReducedMotion();

  return (
    <section className="relative pt-36 md:pt-[164px] pb-16 md:pb-20 flex flex-col min-h-[92vh]" data-testid="kinetic-hero">
      <div className="mx-auto w-full max-w-[1400px] px-6 md:px-12 flex-1 grid lg:grid-cols-12 gap-12 items-center relative z-10">
        <div className="lg:col-span-6 flex flex-col justify-center">
        <MaskedLine delay={0.1}>
          <span className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-fog">
            <Link to="/research" data-testid="hero-keyword-research" className="link-editorial hover:text-ink transition-colors duration-300">Research</Link>
            <span className="text-leaf mx-2" aria-hidden="true">·</span>
            <Link to="/practice" data-testid="hero-keyword-practice" className="link-editorial hover:text-ink transition-colors duration-300">Practice</Link>
            <span className="text-leaf mx-2" aria-hidden="true">·</span>
            <Link to="/community" data-testid="hero-keyword-impact" className="link-editorial hover:text-ink transition-colors duration-300">Impact</Link>
          </span>
        </MaskedLine>

        <h1 className="font-serif font-medium tracking-tighter leading-[1.0] text-[13vw] sm:text-6xl lg:text-[4.4rem] xl:text-[4.9rem] mt-8" data-testid="hero-headline">
          <MaskedLine delay={0.28}>Understanding change</MaskedLine>
          <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span
              initial={reduced ? { opacity: 0 } : { y: "115%", rotate: 1.5, filter: "blur(6px)" }}
              animate={reduced ? { opacity: 1 } : { y: "0%", rotate: 0, filter: "blur(0px)" }}
              transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="block"
            >
              <span className="italic text-ember">beyond</span> the technology.
            </motion.span>
          </span>
        </h1>

        <div className="mt-10 max-w-xl">
          <MaskedLine delay={0.8}>
            <p className="text-base md:text-lg text-fog leading-relaxed" data-testid="hero-thesis">
              We investigate how technological change reshapes human capability, organisations and
              society, and what emerges from those interactions.
            </p>
          </MaskedLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/research"
            data-testid="hero-explore-research-btn"
            className="group inline-flex items-center gap-3 bg-ember text-bone px-8 py-4 text-sm tracking-wide hover:bg-ink transition-colors duration-300"
          >
            Explore our research
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
          <a
            href="mailto:contact@mentheris.com"
            data-testid="hero-contact-btn"
            className="link-editorial inline-flex items-center px-2 py-4 text-sm tracking-wide"
          >
            Start a conversation
          </a>
        </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.6 }}
          className="lg:col-span-6 relative"
        >
          <HeroWave />
        </motion.div>
      </div>
    </section>
  );
}
